import React, { useMemo } from 'react';
import { ItemListOption } from '@/contexts/item-context-types';
import { LIST_MARGIN_HORIZONTAL, TYPOGRAPHY } from '@/styles';
import { Props as ListItemProps } from '@/components/list-item';
import { OrderableItemLabelType } from '@/enums';
import { OrderableItemList } from './orderable-item-list';
import { OrderableItemListItemWithRowQuantity } from './orderable-item-list-item';
import { StyleProp, StyleSheet, View, ViewStyle } from 'react-native';
import { useAdaptAndGroupVariantPacks } from '@/hooks/use-adapt-and-group-item-list-options';
import { useItemDetailContext } from '@/contexts/item-detail-context';
import { useNewOrderActionsContext } from '@/contexts/new-order-actions-context';
import { useSmallLayoutSizeValue } from '@/contexts/layout-context';
import { VolumePromotion } from '@/utils/volume-promotions-types';
import { VolumePromotionBanner } from './volume-promotion-banner';

interface Props {
  listContainerStyle?: StyleProp<ViewStyle>;
  promotion: VolumePromotion;
}

export const VolumePromotionDetailComponent: React.FC<Props> = ({
  listContainerStyle,
  promotion,
}) => {
  const adaptAndGroupVariantPacks = useAdaptAndGroupVariantPacks();
  const { debouncedUpdateQuantity } = useNewOrderActionsContext();
  const { selectItemListOption } = useItemDetailContext();

  const listData = useMemo(() => {
    if (!promotion.promotedVariantPacks.length) {
      return [];
    }

    const variantPacks = promotion.promotedVariantPacks.map(
      (promotedVp) => promotedVp.variantPack,
    );

    return adaptAndGroupVariantPacks(variantPacks, promotion.uuid);
  }, [
    adaptAndGroupVariantPacks,
    promotion.promotedVariantPacks,
    promotion.uuid,
  ]);

  const listItemProps: Partial<ListItemProps<ItemListOption>> = useMemo(() => {
    const onSelect = (option: ItemListOption) => {
      selectItemListOption(option, { source: 'promotion_details' });
    };

    return {
      onSelect,
      renderMainContent: (option) => {
        return (
          <OrderableItemListItemWithRowQuantity
            isFocused={false}
            onOptionsPress={onSelect}
            option={option}
            showListView={false}
            showSingleOption
            source="promotion_details"
            updateQuantity={(updateQuantityData) => {
              debouncedUpdateQuantity(updateQuantityData, {
                source: 'promotion_details',
              });
            }}
            visibleLabels={[OrderableItemLabelType.Availability]}
          />
        );
      },
    };
  }, [debouncedUpdateQuantity, selectItemListOption]);

  const headerStyle = useSmallLayoutSizeValue(null, styles.headerLarge);

  return (
    <View style={[styles.listContainer, listContainerStyle]}>
      <OrderableItemList
        listData={listData}
        ListHeaderComponent={
          <View style={headerStyle}>
            <VolumePromotionBanner
              marketingTextStyle={TYPOGRAPHY.h2}
              promotion={promotion}
              style={styles.banner}
            />
          </View>
        }
        listItemProps={listItemProps}
        listItemSeparatorStyle={styles.itemSeparator}
        renderListHeaderAsListItem
        showListView
      />
    </View>
  );
};

const styles = StyleSheet.create({
  banner: {
    height: 204,
  },
  headerLarge: {
    marginBottom: 12,
    marginHorizontal: LIST_MARGIN_HORIZONTAL,
  },
  itemSeparator: {
    marginHorizontal: LIST_MARGIN_HORIZONTAL,
  },
  listContainer: {
    flex: 1,
  },
});
